import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner'
import SvgX from '@jotta/ui/SvgX'
import { useEffect, type ComponentProps } from 'react'
import {
  removeBackgroundJobsByActionId,
  useBackgroundJobActions,
} from '@jotta/hooks'
import { getToastContent } from './backgroundJobToasts'
import { motion } from 'framer-motion'
import Spinner from '../Elements/Spinner/Spinner'
import { t } from '@lingui/macro'
import { Button } from '../Buttons/Button/Button'

export type ToastStatus = 'IN_PROGRESS' | 'SUCCESS' | 'FAILED'

type ToastOptions = {
  id?: string | number
  duration?: number
  onDismiss?: () => void
  actionButton?: {
    label: string
    onClick: () => void
  }
}

export type ToastContent = {
  minHeight?: number // this is used as a workaround for state changes in headless sonner toasts that needs to change in size. A github issue has been created for this: https://github.com/emilkowalski/sonner/issues/616
  message: string
  isLoading?: boolean
  isCloseable?: boolean
}

const useBackgroundJobToasts = () => {
  const actions = useBackgroundJobActions()
  useEffect(() => {
    actions.forEach(({ actionId, jobType, status }) => {
      showToast({
        ...getToastContent(jobType, status),
        id: actionId,
        onDismiss: () => removeBackgroundJobsByActionId(actionId),
      })
    })
  }, [actions])
}

export const ToastProvider = ({
  ...props
}: ComponentProps<typeof SonnerToaster>) => {
  useBackgroundJobToasts()
  return <SonnerToaster {...props} />
}

const Toast = ({
  id,
  message,
  minHeight,
  isLoading,
  onCloseButtonClick,
  actionButton,
}: ToastContent & ToastOptions & { onCloseButtonClick?: () => void }) => (
  <motion.div
    id={id?.toString()}
    style={{ minHeight: minHeight || 40 }}
    className="flex w-full min-w-[258px] items-center justify-between gap-2 self-center rounded-[4px] border border-light bg-primary px-4 py-1 shadow-md"
    layout
    transition={{
      delay: 0.1,
      layout: {
        type: 'spring',
        duration: 0.15,
        ease: 'easeInOut',
      },
    }}
  >
    <p className="body-sm-ln">{message}</p>
    <div className="flex items-center gap-2">
      {actionButton?.label && actionButton?.onClick && (
        <button className="btn btn-ghost" onClick={actionButton.onClick}>
          {actionButton.label}
        </button>
      )}
      {isLoading && <Spinner size="small" />}
      {onCloseButtonClick && (
        <Button
          className="btn btn-ghost size-5 p-0 hover:bg-transparent"
          icon={SvgX}
          onClick={onCloseButtonClick}
          aria-label={t`Close toast`}
        />
      )}
    </div>
  </motion.div>
)

export const dismissToast = (id: string | number) => {
  sonnerToast.dismiss(id)
}

export function showToast(toast: ToastContent & ToastOptions) {
  const handleActionButtonClick = () => {
    toast?.actionButton?.onClick()
  }
  const handleClose = () => {
    toast?.onDismiss?.()
    toast?.id && dismissToast(toast.id)
  }

  return sonnerToast.custom(
    (toastId: string | number) => (
      <Toast
        key={toastId}
        id={toastId}
        message={toast.message}
        minHeight={toast.minHeight}
        isLoading={toast.isLoading}
        onCloseButtonClick={toast.isCloseable ? handleClose : undefined}
        actionButton={
          toast.actionButton
            ? {
                label: toast.actionButton.label,
                onClick: handleActionButtonClick,
              }
            : undefined
        }
      />
    ),
    {
      duration: toast.duration ?? (toast.isLoading ? Infinity : 20000),
      position: 'bottom-left',
      id: toast.id,
      onDismiss: handleClose,
    },
  )
}
